import { customElement } from 'lit/decorators.js';
import { html, LitElement } from 'lit';
import { LangIconNotificationSignal } from '../../../../localization/webmodule-common/other/ui/icons/icon-notification-signal-lang';

/**
 * Connection state enum for the notification system
 */
export enum ConnectionState {
  /** Initial state - connection status not yet determined */
  Unknown = 'unknown',
  /** System is connected and working normally */
  Online = 'online',
  /** System is offline - network or server unreachable */
  Offline = 'offline',
  /** Service is restarting - planned maintenance, will auto-recover */
  ServiceRestarting = 'service-restarting'
}

export function notificationSignal(value: boolean) {
  GlobalNotificationManager.getInstance().setUpdating(value);
}
export function staleIcon() {
  html`<icon-stale .active=${true} class="me-2"></icon-stale>`;
}
export class GlobalNotificationManager {
  private static _instance: { [key: string]: GlobalNotificationManager } = {};
  private listeners: LitElement[] = [];
  private _updatingCount: number = 0;
  private _connectionState: ConnectionState = ConnectionState.Unknown;
  private _connectionStateListeners: ((state: ConnectionState) => void)[] = [];

  public static getInstance(instanceName?: string): GlobalNotificationManager {
    if (GlobalNotificationManager._instance[instanceName ?? 'default'] === undefined)
      GlobalNotificationManager._instance[instanceName ?? 'default'] = new GlobalNotificationManager();
    return this._instance[instanceName ?? 'default'];
  }
  public register(listener: LitElement) {
    this.listeners = [...this.listeners.filter(x => x !== listener), listener];
  }
  public unRegister(listener: LitElement) {
    this.listeners = this.listeners.filter(x => x !== listener);
  }
  public setUpdating(value: boolean) {
    if (value) this._updatingCount++;
    else this._updatingCount--;
    this.listeners.forEach(x => x.requestUpdate());
  }

  /**
   * Get the current connection state
   */
  public get connectionState(): ConnectionState {
    return this._connectionState;
  }

  /**
   * Set the connection state and notify listeners
   */
  public set connectionState(value: ConnectionState) {
    if (this._connectionState !== value) {
      this._connectionState = value;
      this.listeners.forEach(x => x.requestUpdate());
      this._connectionStateListeners.forEach(listener => listener(value));
    }
  }

  /**
   * Subscribe to connection state changes
   * @returns Unsubscribe function
   */
  public onConnectionStateChange(listener: (state: ConnectionState) => void): () => void {
    this._connectionStateListeners.push(listener);
    return () => {
      this._connectionStateListeners = this._connectionStateListeners.filter(l => l !== listener);
    };
  }

  private _online: boolean | undefined = undefined;
  public get online(): boolean {
    return this._online ?? false;
  }
  public set online(value: boolean) {
    const wasUndefined = this._online === undefined;
    const wasOnline = this._online;
    this._online = value;
    // Update connection state based on online status
    this._connectionState = value ? ConnectionState.Online : ConnectionState.Offline;
    this.listeners.forEach(x => x.requestUpdate());
    // Only notify listeners if we had a previous known state (not initial undefined)
    if (!wasUndefined && wasOnline !== value) {
      this._connectionStateListeners.forEach(listener => listener(this._connectionState));
    }
  }
  public get updating(): boolean {
    return this._updatingCount > 0;
  }

  /**
   * Mark the system as in service restart mode
   * This is a special offline state that indicates planned maintenance
   */
  public setServiceRestarting(): void {
    this._online = false;
    this.connectionState = ConnectionState.ServiceRestarting;
  }

  /**
   * Check if the system is in service restart mode
   */
  public get isServiceRestarting(): boolean {
    return this._connectionState === ConnectionState.ServiceRestarting;
  }
}
@customElement('icon-notification-signal')
export class IconNotificationSignal extends LitElement {
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected get updating(): boolean {
    return GlobalNotificationManager.getInstance().updating;
  }
  protected get online(): boolean {
    return GlobalNotificationManager.getInstance().online;
  }
  connectedCallback(): void {
    super.connectedCallback();
    GlobalNotificationManager.getInstance().register(this);
  }
  disconnectedCallback(): void {
    GlobalNotificationManager.getInstance().unRegister(this);
    super.disconnectedCallback();
  }
  private _offline = html`<i class="fa-solid fa-network-wired text-danger"></i>`;
  private _online = html`<i class="fa-solid fa-network-wired text-primary"></i>`;
  private _updating = html`<webmodule-animate play name="heartBeat"
    ><i class="fa-solid fa-arrows-rotate text-success"></i
  ></webmodule-animate>`;
  render() {
    const legend = html`<div class="flex-nowrap align-self-start align-items-start">
        <span class="colpx-20 flex-grow-0 me-2">${this._online}</span>
        <span class="flex-shrink-1">${LangIconNotificationSignal.systemOnline()}</span>
      </div>
      <div class="flex-nowrap align-self-start align-items-start">
        <span class="colpx-20 flex-grow-0 me-2">${this._offline}</span>
        <span class="flex-shrink-1">${LangIconNotificationSignal.systemOffline()}</span>
      </div>
      <div class="flex-nowrap align-self-start align-items-start">
        <span class="colpx-20 flex-grow-0 me-2">${this._updating}</span>
        <span class="flex-shrink-1">${LangIconNotificationSignal.receivingUpdates()}</span>
      </div> `;

    let icon;
    if (!this.online) {
      icon = this._offline;
    }
    if (this.online && this.updating) {
      icon = this._updating;
    } else {
      icon = this._online;
    }

    return html` <webmodule-tooltip placement="left" class="table-header-tooltip" hoist>
      <div slot="content">${legend}</div>
      <span>${icon}</span>
    </webmodule-tooltip>`;
  }
}
